// Core
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import { Chip, Crumbs, PageHead } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import LoadsTableSection from "components/LoadsTableSection";
import UploadHelpButton from "components/UploadHelpButton";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetTaskStatusByIdQuery } from "state/tasks/tasksApiSlice";
import { selectActiveTasksByType } from "state/tasks/tasksSlice";

// Utils
import { handleFileUpload } from "utils/handlers";
import useTaskStatusEffect from "utils/hooks/useTaskStatusEffect";

/* Standard internal-measurements page: title + template/upload actions with
   async-task polling, and a server-paginated rec table. `noWrapper` renders
   it as an embeddable section (inside SideTabs) instead of a full page. */
function LoadsPage({
  title,
  subtitle,
  taskType,
  templateHref,
  fileType,
  addMutation,
  table,
  noWrapper,
}) {
  const dispatch = useDispatch();
  const community = useSelector(selectCurrentCommunity);
  const activeTasks = useSelector(selectActiveTasksByType(taskType));
  const { t } = useTranslation(["internalLoads", "erse", "sidenav"]);

  // CREATE (csv upload through the async task pipeline)
  const [addLoads] = addMutation();
  const onUpload = handleFileUpload(addLoads, community.id, dispatch, taskType);

  // Task status polling while an upload task is active
  const {
    data: taskStatus,
    isFetching,
    isSuccess,
  } = useGetTaskStatusByIdQuery(
    { taskId: activeTasks[0]?.id, communityId: community.id },
    {
      pollingInterval: 3000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      skip: activeTasks.length === 0,
    }
  );
  useTaskStatusEffect({
    isSuccess,
    isFetching,
    taskStatus,
    activeLoadsTasks: activeTasks,
    dispatch,
    TASK_TYPE: taskType,
  });

  const isUploading = activeTasks.length > 0;
  const uploadId = `loads-upload-${taskType}`;

  const actions = (
    <div className="flex items-center gap-[10px] flex-wrap">
      {isUploading && (
        <Chip tone="amber">
          <Icon name="hourglass_top" style={{ fontSize: 13 }} />
          {t("uploading", { defaultValue: "A carregar…" })}
        </Chip>
      )}
      <a className="rec-btn-ghost rec-btn-sm no-underline" href={templateHref}>
        <Icon name="download" style={{ fontSize: 16 }} />
        {t("list.buttons.template", { ns: "erse", defaultValue: "Template" })}
      </a>
      <UploadHelpButton />
      <label
        htmlFor={uploadId}
        className="rec-btn-ghost rec-btn-sm"
        style={isUploading ? { opacity: 0.4, pointerEvents: "none" } : undefined}
      >
        <Icon name="upload_file" style={{ fontSize: 16 }} />
        {t("upload", { defaultValue: "Carregar dados" })}
        <input id={uploadId} type="file" accept={fileType} hidden onChange={onUpload} />
      </label>
    </div>
  );

  const body = <LoadsTableSection {...table} />;

  if (noWrapper) {
    return (
      <div>
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <h3 className="rec-section-title !mb-0">
              <Icon name="electric_meter" />
              {title}
            </h3>
            <p className="text-[12.5px] text-ink-3 m-0 mt-1">{subtitle}</p>
          </div>
          {actions}
        </div>
        {body}
      </div>
    );
  }

  return (
    <div>
      <Crumbs
        items={[
          { label: community?.name || "—" },
          { label: t("internalloads", { ns: "sidenav", defaultValue: "Medições Internas" }) },
          { label: title },
        ]}
      />
      <PageHead title={title} subtitle={subtitle} actions={actions} />
      {body}
    </div>
  );
}

LoadsPage.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  taskType: PropTypes.string.isRequired,
  templateHref: PropTypes.string.isRequired,
  fileType: PropTypes.string,
  addMutation: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  table: PropTypes.object.isRequired,
  noWrapper: PropTypes.bool,
};

LoadsPage.defaultProps = {
  subtitle: null,
  fileType: ".csv",
  noWrapper: false,
};

export default LoadsPage;
